import { Injectable } from '@angular/core';
import { Custom_RequestMethod } from '../common-service';
import { isPresent } from '../common-service';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, distinctUntilChanged } from 'rxjs/operators';
import { AuthenticationService } from '../../authentication-service';
import { SessionInfo } from '../../data/authentication-info';
// import {CourseHoleInfo, CourseInfo} from '../../data/club-course';
import { LeanScorecard } from '../../data/lean-scorecard';
// import {PlayerScore, ScorecardList} from '../../data/scorecard';
// PlainScoreCard, PlayerRoundScores,
import { SearchCriteria } from '../../data/search-criteria';
import { ServerResult } from '../../data/server-result';
import * as global from '../../globals';
import { JsonService } from '../../json-util';
import { MessageDisplayUtil } from '../../message-display-utils';
import { SessionDataService } from '../../redux/session/session-data-service';
import { RemoteHttpService } from '../../remote-http';
import { ContentType, RemoteRequest } from '../../remote-request';
import { MyGolfStorageService } from '../../storage/mygolf-storage.service';
import { Preference } from '../../storage/preference';
import { ConnectionService } from '../connection-service/connection-service';
import { EventLogService } from '../eventlog-service/eventlog-service';
import { ScorecardStorageService } from './scorecard-storage-service';
import {
  PlainScoreCard,
  PlayerRoundScores,
  PlayerScore,
  ScorecardList,
  CourseHoleInfo,
  CourseInfo,
} from '../../data/mygolf.data';
import { HttpEvent } from '@angular/common/http';
/*
 Generated class for the ScorecardService provider. This class handles the
 scorecard related services. The client codes must use this service.

 See https://angular.io/docs/ts/latest/guide/dependency-injection.html
 for more info on providers and Angular 2 DI.
 */

@Injectable()
export class ScorecardService {
  private static CurrentScorecard: string = 'CurrentScorecard';
  private searchCriteria: SearchCriteria;
  private scorecardType: string;
  private startDate: Date;
  private endDate: Date;
  private session: SessionInfo;
  constructor(
    public http: RemoteHttpService,
    private eventLogService: EventLogService,
    private scorecardStorageService: ScorecardStorageService,
    private auth: AuthenticationService,
    private sessionService: SessionDataService,
    private connService: ConnectionService,
    private pref: Preference,
    private storage: MyGolfStorageService,
    private requestMethod: Custom_RequestMethod
  ) {
    this.searchCriteria = {
      searchType: 'all',
      onlyParticipating: true,
    };
    this.scorecardType = 'ALL';

    this.sessionService
      .getSession()
      .pipe(distinctUntilChanged())
      .subscribe((session: SessionInfo) => {
        this.session = session;
      });
    this.pref.getPref('ScorecardSearchFilter').subscribe(
      (data: SearchCriteria) => {
        if (data) {
          this.searchCriteria = data;
        }
      },
      error => {}
    );
    this.pref.getPref('ScorecardSearchScorecardType').subscribe(
      data => {
        if (data) {
          this.scorecardType = data;
        }
      },
      error => {}
    );
  }

  public getSearchCriteria(): SearchCriteria {
    return this.searchCriteria;
  }

  public getScorecardType(): string {
    return this.scorecardType;
  }

  public setSearchCriteria(criteria: SearchCriteria, scorecardType: string = 'ALL') {
    this.searchCriteria = criteria;
    this.scorecardType = scorecardType;
    this.pref.setPref('ScorecardSearchFilter', criteria);
    this.pref.setPref('ScorecardSearchScorecardType', scorecardType);
  }

  public setFilterStartDate(startDate: Date) {
    this.startDate = startDate;
  }

  public setFilterEndDate(endDate: Date) {
    this.endDate = endDate;
  }

  /**
   * Gets the scorecard for a given round. The player id is automatically
   * populated as header while requesting the scorecard
   * @param gameRoundId The ID of the game round
   * @returns {Observable<R>} Returns the observable for PlainScoreCard
   */
  public getScorecard(gameRoundId: number): Observable<PlainScoreCard> {
    const request = new RemoteRequest(
      global.ServerUrls.scorecardGet,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        gameRoundId: gameRoundId,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const scorecard: PlainScoreCard = resp;
          ScorecardService.transform(scorecard);
          return scorecard;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  /**
   * Gets the scorecard for a given player in a given competition round.
   * The return scorecard returns either scores for all players in the flight or
   * for the player based on the fourth parameter "onlyForPlayer".
   * @param competitionId The ID of the competition
   * @param roundNo The competition round number
   * @param playerId The id of the player
   * @param onlyForPlayer If set to true, then scorecard for the player only returned.
   *  If false, then scores for all the players in the flight are included. Default is false
   * @returns {Observable<R>}
   */
  public getCompetitionScorecard(
    competitionId: number,
    roundNo: number,
    playerId: number,
    onlyForPlayer: boolean = false
  ) {
    const request = new RemoteRequest(
      global.ServerUrls.competitionScorecardGet,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        competitionId: competitionId,
        roundNo: roundNo,
        playerId: playerId,
        onlyForPlayer: onlyForPlayer,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const scorecard: PlainScoreCard = resp;
          ScorecardService.transform(scorecard);
          return scorecard;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  /**
   * Search the scorecards for a given player
   * @param searchText
   * @param pageNumber
   */
  public searchScorecard(searchText: string, pageNumber: number = 1): Observable<ScorecardList> {
    const request = this.createSearchRequest(searchText, pageNumber, this.scorecardType);

    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const scorecardList: ScorecardList = resp;
          if (scorecardList) {
            scorecardList.scorecards.forEach((sc: PlainScoreCard) => {
              ScorecardService.transform(sc);
            });
          }
          return scorecardList;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  public getCurrentScorecard(type?: string): Observable<PlainScoreCard> {
    return this.pref.getPref(this._currScorecardKey(type)).pipe(
      map((scoreCard: PlainScoreCard) => {
        console.debug('get current scorecard : ', type, this._currScorecardKey(type), scoreCard);
        if (
          scoreCard?.currentHole !== scoreCard?.startingHole &&
          scoreCard?.currentHole !== undefined
        ) {
          this.setScoringCurrentHole(scoreCard?.currentHole);
        } else if (scoreCard) {
          this.setScoringCurrentHole(scoreCard?.startingHole);
        }
        //    if (typeof scoreCard.playedOn === "string") {
        //        scoreCard.playedOn = moment(scoreCard.playedOn).toDate();
        //    }
        return scoreCard;
      })
    );
  }

  public clearScorecard(type?: string): Observable<PlainScoreCard> {
    return this.pref.setPref(this._currScorecardKey(type));
  }

  private _currScorecardKey(type?: string): string {
    const playerId = this.session.playerId;
    if (0 && type === 'NormalGame') {
      return ScorecardService.CurrentScorecard + '.' + playerId + '.NormalGame';
    } else {
      return ScorecardService.CurrentScorecard + '.' + playerId;
    }
  }

  /**
   * Saves the current game going on with the latest scores.
   * If the scorecard is for competition, then it will be synced
   * immediately
   * @param scorecard
   * @param localOnly Specifies whether to save the scorecard only locally. The sync only
   * should happen when you change the scoring holes
   */
  public saveCurrentScorecard(
    scorecard: PlainScoreCard,
    localOnly: boolean = false
  ): Observable<PlainScoreCard> {
    const _scorecardType = ''; // localOnly?'NormalGame':'';
    if (!isPresent(this.session)) {
      return;
    }
    if (this.session === undefined) {
      return;
    }
    if (this.session?.playerId === undefined) {
      return;
    }
    this.pref.setPref(this._currScorecardKey(_scorecardType), scorecard);
    //Store in permanent storage
    this.scorecardStorageService.saveCompetitionScorecard(scorecard, this.session.playerId);

    //If the scorecard is competition or To save it immediately
    if (!localOnly && scorecard.competition && scorecard.dirty) {
      // scorecard.dirty = false;
      return this.saveScorecard(scorecard, false);
    } else {
      return of(scorecard);
    }
    // //Fire an event for scorecard change
  }

  /**
   * Cancels given game and deletes from the local storage
   * @param scorecard The scorecard for cancelling.
   */
  public cancelGame(scorecard: PlainScoreCard) {
    //Can a competition game round can be cancelled?
    // this._deleteCurrentScorecard();
  }

  /**
   * Finish the ongoing game. This will generate GameFinished event
   * @param scorecard The scorecard of the game finished
   */
  public finsihGame(scorecard: PlainScoreCard): Observable<PlainScoreCard> {
    //Now store the game with its client key
    scorecard.finished = true;
    if (scorecard.competition) {
      //Save the scorecard and finish the scoring
      return this.saveScorecard(scorecard, true)
        .pipe(
          map(
            (scorecard: PlainScoreCard) =>
              // this._deleteCurrentScorecard();

              scorecard
          )
        )
        .pipe(catchError(this.http.handleError));
    } else {
      return this.saveNormalGameScorecard(scorecard).pipe(
        map(
          (saved: PlainScoreCard) =>
            // this._deleteCurrentScorecard();

            saved
        )
      );
    }
  }

  /**
   * Merge the scorecard contents from source to target. The database IDs are
   * merged from source to target.
   * The scores of players from source scorecard are copied to target if the current
   * player is not scoring for them.
   * @param target The target scorecard. The values will be copied to this
   * @param source The source scorecard. The values from this will be copied
   */
  public static mergeScorecards(target: PlainScoreCard, source: PlainScoreCard, playerId: number) {
    //First copy the database IDs
    target.gameRoundId = source.gameRoundId;
    target.playerRoundScores.forEach((prs: PlayerRoundScores) => {
      //Get the corresponding player round
      const srcPrs = source.playerRoundScores
        .filter((p: PlayerRoundScores) => p.playerId === prs.playerId)
        .pop();
      prs.playerRoundId = srcPrs.playerRoundId;
      prs.backNineNetTotal = srcPrs.backNineNetTotal;
      prs.backNineTotal = srcPrs.backNineTotal;
      prs.frontNineNetTotal = srcPrs.frontNineNetTotal;
      prs.frontNineTotal = srcPrs.frontNineTotal;
      prs.totals.forEach(trgTotals => {
        const srcTotals = srcPrs.totals.filter(t => trgTotals.whichNine === t.whichNine).pop();
        if (srcTotals) {
          trgTotals.netTotal = srcTotals.netTotal;
          trgTotals.grossTotal = srcTotals.grossTotal;
        }
      });
      prs.scores.forEach((ps: PlayerScore) => {
        //Find the corresponding player score
        const srcScore = srcPrs.scores
          .filter(
            (s: PlayerScore) =>
              s.whichNine === ps.whichNine &&
              // s.gameCourseId === ps.gameCourseId
              s.courseId === ps.courseId &&
              s.courseHoleId === ps.courseHoleId
          )
          .pop();
        ps.scorecardId = srcScore.scorecardId;
        ps.gameCourseId = srcScore.gameCourseId;
        if (playerId && prs.scoringPlayerId !== playerId) {
          //Update the scores from source
          ps.actualScore = srcScore.actualScore;
          ps.netScore = srcScore.netScore;
        } else {
          ps.netScore = srcScore.netScore;
        }
      });
    });
    target.courses.forEach((course: CourseInfo) => {
      const srcCourse = source.courses
        .filter(
          (c: CourseInfo) => c.courseId === course.courseId && c.whichNine === course.whichNine
        )
        .pop();
      course.gameCourseId = srcCourse.gameCourseId;
    });
  }

  public static isFlightSame(
    scorecard: PlainScoreCard,
    scorecardFromServer: PlainScoreCard,
    playerId: number
  ): boolean {
    let result = false;
    //Check the players first

    const players = scorecard.playerRoundScores.map(prs => prs.playerId);
    const serverPlayers = scorecardFromServer.playerRoundScores.map(prs => prs.playerId);
    result = ScorecardService.isArrayEquals(players, serverPlayers);
    if (result) {
      //Check the status and scorer for each player
      scorecard.playerRoundScores.forEach(prs => {
        const serverPrs = scorecardFromServer.playerRoundScores
          .filter(sprs => prs.playerId === sprs.playerId)
          .pop();
        if (serverPrs) {
          result =
            result &&
            prs.status === serverPrs.status &&
            prs.scoringPlayerId === serverPrs.scoringPlayerId;
        }
      });
    }

    return result;
  }

  public static isArrayEquals(arrA: Array<any>, arrB: Array<any>): boolean {
    //check if lengths are different
    if (arrA.length !== arrB.length) {
      return false;
    }

    //slice so we do not effect the orginal
    //sort makes sure they are in order
    var cA = arrA.slice().sort();
    var cB = arrB.slice().sort();

    for (var i = 0; i < cA.length; i++) {
      if (cA[i] !== cB[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Saves the normal game scorecard
   * @param scorecard The scorecard to save
   * @returns {Observable<R>}
   */
  public saveNormalGameScorecard(scorecard: PlainScoreCard, teeTime?): Observable<PlainScoreCard> {
    const playedOnDate = scorecard.playedOn;
    const playedOn = moment(scorecard.playedOn).format('YYYY-MM-DD');
    const actualStartTime = scorecard.playerRoundScores.map(prs => prs.actualStartTime).pop();
    const startTime = scorecard.playerRoundScores.map(prs => prs.startTime).pop();
    // scorecard.playerRoundScores.forEach((prs: PlayerRoundScores) => {
    //   prs.startTime = null;
    //   prs.actualStartTime = null;
    // });

    if (
      !scorecard.competition &&
      (!isPresent(scorecard?.playerRoundScores[0]?.startTime) ||
        scorecard?.playerRoundScores[0]?.startTime === undefined ||
        scorecard?.playerRoundScores[0]?.startTime === 'Invalid Date' ||
        scorecard?.playerRoundScores[0]?.startTime === '' ||
        scorecard?.playerRoundScores[0]?.startTime === null)
    ) {
      scorecard.playerRoundScores.forEach(prs => {
        prs.startTime = moment(startTime, 'HH:mm:ss').format('HH:mm'); //moment().format('HH:mm:ss');
      });
    } else if (
      !scorecard.competition &&
      (!isPresent(scorecard?.playerRoundScores[0]?.actualStartTime) ||
        scorecard?.playerRoundScores[0]?.actualStartTime === undefined ||
        scorecard?.playerRoundScores[0]?.actualStartTime === null)
    ) {
      scorecard.playerRoundScores.forEach(prs => {
        prs.actualStartTime = moment(actualStartTime, 'HH:mm:ss').format('HH:mm'); //moment().toDate();
      });
    } else if (!scorecard?.competition) {
      scorecard.playerRoundScores.forEach(prs => {
        prs.actualStartTime = moment().format('HH:mm');
      });
    }
    // if(true) return null;
    scorecard['playedOn'] = null;

    const scorecardStr = JSON.stringify(scorecard);
    scorecard.playedOn = playedOnDate;
    const data = {
      scorecard: scorecardStr,
    };
    if (playedOnDate) {
      data['playedOn'] = playedOn;
    }
    if (startTime) {
      data['startTime'] = moment(startTime, 'HH:mm:ss').format('HH:mm');
    }
    if (actualStartTime) {
      data['actualStartTime'] = moment(actualStartTime, 'HH:mm:ss').format('HH:mm');
    }
    // else data["startTime"] = moment(teeTime).format("HH:mm");

    // if(teeTime) data["startTime"] = moment(teeTime).format("HH:mm");
    // else data["startTime"] = moment(startTime).format("HH:mm");
    const updParam =
      '?playedOn=' +
      data['playedOn'] +
      '&startTime=' +
      data['startTime'] +
      '&actualStartTime=' +
      data['actualStartTime'];

    const request = new RemoteRequest(
      global.ServerUrls.saveNormalGame + updParam,
      this.requestMethod.post,
      ContentType.URL_ENCODED_FORM_DATA,
      data
    );

    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const saved: PlainScoreCard = resp;
          //Now merge these two scorecards
          ScorecardService.mergeScorecards(scorecard, saved, this.session.playerId);
          scorecard.playerRoundScores.forEach((prs: PlayerRoundScores) => {
            prs.startTime = startTime;
            prs.actualStartTime = actualStartTime;
            const savedP = saved.playerRoundScores
              .filter(savedPrs => prs.playerRoundId === savedPrs.playerRoundId)
              .pop();
            if (savedP) {
              prs.backNineNetTotal = savedP.backNineNetTotal;
              prs.backNineTotal = savedP.backNineTotal;
              prs.frontNineNetTotal = savedP.frontNineNetTotal;
              prs.frontNineTotal = savedP.frontNineTotal;
            }
          });
          scorecard.dirty = false;
          scorecard.backNineTotal = saved.backNineTotal;
          scorecard.frontNineTotal = saved.frontNineTotal;
          scorecard.totalScore = saved.totalScore;
          return scorecard;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  /**
   * Deletes the scorecard for a given normal game
   * @param roundId The ID of the round for which scores will be deleted
   * @param deleteAll Delete all the scores you have scored
   * @returns {Observable<R>}
   */
  public deleteNormalGameScorecard(
    roundId: number,
    deleteAll: boolean = false
  ): Observable<boolean> {
    const request = new RemoteRequest(
      global.ServerUrls.deleteNormalGameScorecar,
      this.requestMethod.post,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        roundId: roundId,
        deleteAll: deleteAll,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const result: ServerResult = resp;
          return result.success;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  public syncCompetitionScorecard(
    scorecard: LeanScorecard | any,
    finishRound: boolean,
    compressed: boolean,
    playerId?: number
  ): Observable<ServerResult> {
    const scorecardJson = JSON.stringify(scorecard);
    const hdrs = {};
    console.debug('[Player-Id] [sync]', playerId);
    if (isPresent(playerId)) {
      hdrs['Player-Id'] = playerId;
    }
    const request = new RemoteRequest(
      global.ServerUrls.competitionScorecardSync,
      this.requestMethod.post,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        finishRound: finishRound,
        scorecard: scorecardJson,
        compressed: compressed,
      },
      hdrs
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const result: ServerResult = resp;
          return result;
        })
      )
      .pipe(catchError(this.http.handleError));
  }
  /**
   * Saves the competition scorecard in the server. The competition scorecard
   * should directly call this method instead of
   * @param scorecard
   */
  public saveScorecard(
    scorecard: PlainScoreCard,
    finishRound: boolean,
    playerId?: number
  ): Observable<PlainScoreCard> {
    scorecard.playerRoundScores.forEach((prs: PlayerRoundScores) => {
      prs.actualStartTime = null;
      prs.startTime = null;
    });

    // if (
    //   !scorecard.competition &&
    //   (!isPresent(scorecard?.playerRoundScores[0]?.startTime) ||
    //     scorecard?.playerRoundScores[0]?.startTime === undefined ||
    //     scorecard?.playerRoundScores[0]?.startTime === 'Invalid Date' ||
    //     scorecard?.playerRoundScores[0]?.startTime === '' ||
    //     scorecard?.playerRoundScores[0]?.startTime === null)
    // ) {
    //   scorecard.playerRoundScores.forEach(prs => {
    //     prs.startTime = moment().format('HH:mm:ss');
    //   });
    // }
    // if (
    //   !scorecard.competition &&
    //   (!isPresent(scorecard?.playerRoundScores[0]?.actualStartTime) ||
    //     scorecard?.playerRoundScores[0]?.actualStartTime === undefined ||
    //     scorecard?.playerRoundScores[0]?.actualStartTime === null)
    // ) {
    //   scorecard.playerRoundScores.forEach(prs => {
    //     prs.actualStartTime = moment().toDate();
    //   });
    // }
    const playedOn = scorecard.playedOn;
    scorecard.playedOn = null;
    const scorecardJson = JSON.stringify(scorecard);
    scorecard.playedOn = playedOn;
    const hdrs = {};
    console.debug('[Player-Id] [save]', playerId);
    if (isPresent(playerId)) {
      hdrs['Player-Id'] = playerId;
    }
    const request = new RemoteRequest(
      global.ServerUrls.competitionScorecardUpdate,
      this.requestMethod.post,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        competitionId: scorecard.competitionId,
        finishRound: finishRound,
        scorecard: scorecardJson,
      },
      hdrs
    );
    scorecard.dirty = false;
    if (finishRound) {
    }
    this.eventLogService.writeEvent(
      'scorecard',
      'Saving scorecard',
      'Syncing scorecard with server'
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          const saved: PlainScoreCard = resp;
          //Now merge these two scorecards
          ScorecardService.mergeScorecards(scorecard, saved, this.session.playerId);
          scorecard.dirty = scorecard.dirty || false;
          scorecard.flightChanged = saved.flightChanged;
          this.eventLogService.writeEvent(
            'scorecard',
            'Saved scorecard',
            'Syncing scorecard with server completed.'
          );
          return scorecard;
        })

        //        .catch((error) => {
        //     let defMsg = (!this.connService.isConnected())?
        //                  "Could not save scorecard. Your internet connection down"
        //                 :"myGolf2u server is unreachable. Could not save scorecard.";
        //     let msg = MessageDisplayUtil.getErrorMessage(error, defMsg);

        //     this.eventLogService.writeEvent("scorecard",
        //         "Saving scorecard failed", msg, "error", JSON.stringify(error));
        //     throw error;
        // }
      )
      .pipe(catchError(this.http.handleError));
  }

  /**
   * Transforms the scorecards and derives the full urls for images and derives the
   * date values from string
   * @param sc The PlainScoreCard instance
   */
  public static transform(sc: PlainScoreCard) {
    JsonService.deriveDates(sc, ['playedOn']);
    if (sc.courses) {
      sc.courses.forEach((ci: CourseInfo) => {
        JsonService.deriveFullUrl(ci, 'photoUrl');
        ci.holes.forEach((h: CourseHoleInfo) => {
          JsonService.deriveFullUrl(h, 'holeImage');
        });
      });
    }
    if (sc.playerRoundScores) {
      sc.playerRoundScores.forEach((prs: PlayerRoundScores) => {
        JsonService.deriveFullUrl(prs, 'photoUrl');
        JsonService.deriveFullUrl(prs, 'thumbnail');
        // JsonService.deriveTime(prs, ["actualStartTime", "startTime"]);
      });
    }
  }

  public static isAllScoresIn(scorecard: PlainScoreCard, scorerId: number): boolean {
    // console.debug("is all scores in ", scorerId)
    const emptyHoles = scorecard.playerRoundScores
      .filter(
        prs =>
          prs.scoringPlayerId === scorerId &&
          !prs.status.toLowerCase().includes('withdraw') &&
          prs.status !== 'W'
      )
      .map(prs => {
        const count = prs.scores.filter(
          s => !isPresent(s.actualScore) || s.actualScore === 0
        ).length;
        return count;
      })
      .reduce((a, b) => a + b, 0);

    return emptyHoles === 0;
  }

  public static isAllScoresEmpty(scorecard: PlainScoreCard, scorerId: number): boolean {
    if (!scorecard) {
      return false;
    }
    const scoringPlayers = scorecard.playerRoundScores.filter(
      prs =>
        prs.scoringPlayerId === scorerId &&
        !prs.statusName?.toLowerCase()?.includes('withdraw') &&
        prs.status !== 'W'
    );
    const emptyHoles = scoringPlayers
      .map(prs => {
        const count = prs.scores.filter(
          s => !isPresent(s.actualScore) || s.actualScore === 0
        ).length;
        return count;
      })
      .reduce((a, b) => a + b, 0);
    const coursePlaying = scorecard?.courses;

    console.debug('is all scores empty : ', scorerId, scoringPlayers, emptyHoles);

    return scoringPlayers?.length * 9 * coursePlaying?.length === emptyHoles;
  }

  public static isAllScoresSubmitted(scorecard: PlainScoreCard, scorerId: number): boolean {
    // console.debug("is all scores in ", scorecard, scorerId)
    const submitted = scorecard.playerRoundScores.filter(
      prs =>
        prs.scoringPlayerId === scorerId &&
        !(
          prs.status.toLowerCase().includes('c') ||
          prs.statusName.toLowerCase().includes('completed')
        )
    )?.length;
    // .map(prs => {
    //   let count = prs.scores.filter(s => !isPresent(s.actualScore) || s.actualScore === 0).length;
    //   return count;
    // })
    // .reduce((a, b) => {
    //   return a + b;
    // }, 0);

    const emptyHoles = scorecard.playerRoundScores
      .filter(
        prs =>
          prs.scoringPlayerId === scorerId &&
          (prs.status.toLowerCase().includes('i') ||
            prs.statusName.toLowerCase().includes('inprogress'))
      )
      //?.length;
      .map(prs => {
        const count = prs.scores.filter(
          s => !isPresent(s.actualScore) || s.actualScore === 0
        ).length;
        return count;
      })
      .reduce((a, b) => a + b, 0);

    // console.debug('is all scores submitted [0] ', submitted, emptyHoles);

    return emptyHoles === 0 && submitted === 0;
  }

  private createSearchRequest(
    searchText?: string,
    pageNo: number = 1,
    scorecardType: string = 'ALL'
  ): RemoteRequest {
    let params = {
      pageNumber: pageNo,
      scorecardType: scorecardType,
    };
    if (isPresent(searchText)) {
      params['searchText'] = searchText;
    }
    if (isPresent(this.startDate)) {
      const str = moment(this.startDate).format('YYYY-MM-DD');
      params['startDate'] = str;
    }
    if (isPresent(this.endDate)) {
      params['endDate'] = moment(this.endDate).format('YYYY-MM-DD');
    }

    if (this.searchCriteria.periodType != 'CUSTOM') {
      params['startDate'] = '';
      params['endDate'] = '';
    }

    params = JsonService.mergeObjects([params, this.searchCriteria]);

    return new RemoteRequest(
      global.ServerUrls.scorecardSearch,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      params
    );
  }

  scoringCurrentHole: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  setScoringCurrentHole(holeNo: number) {
    this.scoringCurrentHole.next(holeNo);
  }

  getScoringCurrentHole() {
    return this.scoringCurrentHole.asObservable();
  }

  // /**
  //  * Deletes the current scorecard
  //  * @private
  //  */
  // private _deleteCurrentScorecard(): Observable<any> {
  //     return this.pref.setPref(this._currScorecardKey());
  // }
}
